import { useEffect, useRef, useState } from 'preact/hooks';
import { useI18n } from '../../hooks/use-i18n';
import { IconX } from './icons';

interface MediaLightboxProps {
  type: 'image' | 'video';
  url: string;
  name?: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DBLCLICK_SCALE = 2.5;
// 每单位 deltaY 的缩放增量(鼠标一格 ~100 → 约 0.15x,触控板更细腻)
const WHEEL_FACTOR = 0.0015;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * 图片/视频放大查看遮罩。
 * - 图片:滚轮缩放(MIN_SCALE~MAX_SCALE)、放大后可拖拽平移、双击在原始/放大间切换;
 *   缩回原始比例时自动复位平移。
 * - 视频:直接 controls 播放,不做缩放。
 * 由父级用 key={url} 挂载,故每次打开/换源都从初始状态开始,无需额外重置逻辑。
 */
export function MediaLightbox({ type, url, name, onClose }: MediaLightboxProps) {
  const { t, dir } = useI18n();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  // 拖拽起点:px/py 为鼠标按下坐标,ox/oy 为按下时的图片偏移
  const dragRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  // Esc 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 滚轮缩放 —— 必须用非 passive 监听才能 preventDefault 阻止宿主页面跟着滚动
  useEffect(() => {
    if (type !== 'image') return;
    const el = imgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale((prev) => {
        const next = clamp(prev - e.deltaY * WHEEL_FACTOR, MIN_SCALE, MAX_SCALE);
        if (next <= MIN_SCALE) setOffset({ x: 0, y: 0 }); // 缩回原始时复位平移
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [type]);

  // 拖拽平移(仅放大后) —— mousemove/up 挂在 document,鼠标拖出图片也能继续
  useEffect(() => {
    if (type !== 'image') return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setOffset({ x: d.ox + (e.clientX - d.px), y: d.oy + (e.clientY - d.py) });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [type]);

  const startDrag = (e: MouseEvent) => {
    if (scale <= MIN_SCALE) return; // 未放大时不拖拽
    dragRef.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  };

  const toggleZoom = () => {
    if (scale > MIN_SCALE) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    } else {
      setScale(DBLCLICK_SCALE);
    }
  };

  const zoomed = scale > MIN_SCALE;

  return (
    <div
      class="dcs-web-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={type === 'image' ? t('web.viewImage') : t('web.playVideo')}
      dir={dir}
      onClick={onClose}
    >
      <button
        type="button"
        class="dcs-web-lightbox-close"
        aria-label={t('web.closeViewer')}
        onClick={onClose}
      >
        <IconX size={24} />
      </button>
      {type === 'image' ? (
        <img
          ref={imgRef}
          class={`dcs-web-lightbox-media dcs-web-lightbox-img${zoomed ? ' is-zoomed' : ''}`}
          src={url}
          alt={name || ''}
          draggable={false}
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          onClick={(e) => e.stopPropagation()}
          onDblClick={(e) => {
            e.stopPropagation();
            toggleZoom();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            startDrag(e);
          }}
        />
      ) : (
        <video
          class="dcs-web-lightbox-media"
          src={url}
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}
